/**
 * Gössl Theme - Product Form v3
 * Handles color swatches, size grid, Swiper gallery, lightbox, and add to cart.
 */

(function() {
  'use strict';

  // Logical size ordering for Größe option
  var SIZE_ORDER = [
    'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL',
    '0', '1', '2', '3', '4', '4+', '5', '5+', '6', '6+',
    '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37', '38',
    '39', '40', '41', '42', '44', '46', '48', '50', '52', '54', '56', '58', '60',
    '62', '64', '86', '92', '98', '104', '110', '116', '122', '128', '134', '140',
    '146', '152', '158', '164', '170', '176'
  ];

  /**
   * Extract color code from linked option value.
   * "Marine 550" → "550", "rosa 61" → "61"
   */
  function extractColorCode(optionValue) {
    if (!optionValue) return null;
    var match = optionValue.match(/\s(\d+)$/);
    return match ? match[1] : null;
  }

  /**
   * a11y #902: the strike (.unavailable) is only grey on screen - a struck size or colour says it in its
   * accessible name too, in the add button's own word: 'sold_out' (the variant exists, none left) or
   * 'unavailable' (this combination does not exist). null clears it. The button's own name is kept in
   * data-a11y-name; a button without an aria-label (sticky bar) is named by its text.
   */
  function setUnavailable(btn, reason) {
    btn.classList.toggle('unavailable', !!reason);
    if (!('a11yName' in btn.dataset)) btn.dataset.a11yName = btn.getAttribute('aria-label') || '';
    var name = btn.dataset.a11yName;
    if (reason) {
      var word = reason === 'sold_out'
        ? window.g26tText('products.product.sold_out', 'Ausverkauft')
        : window.g26tText('products.product.unavailable', 'Nicht verfügbar');
      btn.setAttribute('aria-label', (name || btn.textContent.trim()) + ', ' + word);
    } else if (name) {
      btn.setAttribute('aria-label', name);
    } else {
      btn.removeAttribute('aria-label');
    }
  }

  class ProductForm {
    constructor(section) {
      this.section = section;
      this.form = section.querySelector('[data-product-form]');
      this.productJson = section.querySelector('[data-product-json]');

      if (!this.form || !this.productJson) return;

      this.product = JSON.parse(this.productJson.textContent);
      this.variantIdInput = this.form.querySelector('[data-variant-id]');
      this.addToCartBtn = this.form.querySelector('[data-add-to-cart]');

      // Vorbestellung (pre-order). main-product.liquid renders this hidden input
      // only for products carrying the "Vorbestellung" selling plan. We POST to
      // /cart/add.js instead of submitting the form, so the plan has to be read
      // out and put on the line item by hand — without it the line books as an
      // ordinary oversold sale with no pre-order marking anywhere.
      var sellingPlanInput = this.form.querySelector('[data-selling-plan]');
      this.preorderPlanId = sellingPlanInput ? sellingPlanInput.value : '';
      // r187 (Birgit 01.10.2026): pre-order per size - see isPreorderVariant / syncPreorderUi.
      this.sellingPlanInput = sellingPlanInput;
      this.preorderBox = section.querySelector('[data-preorder]');
      this.preorderNotes = {};
      var notesEl = section.querySelector('[data-preorder-notes]');
      if (notesEl) { try { this.preorderNotes = JSON.parse(notesEl.textContent); } catch (e) {} }
      this.priceContainer = section.querySelector('[data-product-price]');
      this.skuElement = section.querySelector('[data-product-sku]');
      this.selectedColorLabel = section.querySelector('[data-selected-color]');
      this.inventory = {};
      var invEl = section.querySelector('[data-product-inventory]');
      if (invEl) { try { this.inventory = JSON.parse(invEl.textContent); } catch (e) {} }
      // 22.10.2026: the low-stock line and the sizes with 1-4 pieces left (snippets/low-stock.liquid)
      this.lowStock = section.querySelector('[data-low-stock]');
      this.lowStockMap = {};
      var lowEl = section.querySelector('[data-product-low-stock]');
      if (lowEl) { try { this.lowStockMap = JSON.parse(lowEl.textContent) || {}; } catch (e) {} }

      // Swiper instances
      this.mainSwiper = null;
      this.thumbsSwiper = null;

      // Lightbox elements
      this.lightbox = section.querySelector('[data-lightbox]');
      this.lightboxSwiper = null;
      this.lightboxCounter = section.querySelector('[data-lightbox-counter]');

      // 22.10.2026 (SJ: "a blue shirt is clicked, it might go to a yellow shirt to start with"): what the link named.
      // ?variant= names a colour and a size; with size=open (snippets/product-colour-url.liquid - cards, rows, the
      // quick view) only the colour, and the size stays the visitor's (size memory) as on a plain visit - or none
      // at all (SJ's card a, same day: no size of the page's own choosing). The named colour stays on the page even
      // when it is sold out or has no photo: the hide passes below leave its swatch.
      var requested = new URL(window.location.href);
      var requestedId = requested.searchParams.get('variant');
      this._urlVariant = requestedId ? (this.product.variants.find(function(v) { return String(v.id) === requestedId; }) || null) : null;
      this._sizeOpen = requested.searchParams.get('size') === 'open';
      var farbeAt = (this.product.options || []).indexOf('Farbe');
      this._urlColour = this._urlVariant && farbeAt > -1 ? this._urlVariant.options[farbeAt] : null;

      // Apply URL variant param BEFORE reading active state
      this.applyUrlVariant();

      this.currentVariant = this.getVariantFromOptions();

      this.hideNoImageColors();
      this.hideOutOfStockColors();
      this.keepColourSelected();
      // 06.10.2026: a ?variant= link (or the featured variant) can name a colour
      // these two passes hide - no photo or sold out. They move the selection to the first
      // visible colour, and the form now follows it the way a click on that swatch would:
      // the same size in that colour when it exists, otherwise the no-variant state. Before,
      // the form kept the hidden colour's variant, so the page showed one colour and the cart
      // got the other (94558 Sneaker, ?variant= braun 80 showed gold-schwarz, added braun).
      // onVariantChange() without an option name: the gallery is filtered further down,
      // once Swiper has loaded, from the swatch that is active by then.
      if (this._colourMoved) this.onVariantChange();
      this.sortSizeButtons();
      this.updateSizeAvailability();
      this.updateColorAvailability();
      // SJ 22.10.2026 (card a): no size of the page's own choosing any more - selectDefaultSize() picked the middle
      // size whenever a colour change left none. The size comes from the link (a plain ?variant=), the visitor's
      // memory (applySizeMemory below) or the visitor; until then the button asks for it.

      // Update UI to match the selected variant
      this.updatePrice();
      this.updateSku();
      this.updateAddToCartButton();
      this.updateStockStatus();

      // Swiper may not be loaded yet (CDN script at bottom of body)
      var self = this;
      if (typeof Swiper !== 'undefined') {
        this.initSwiper();
        this.filterSlidesByColor();
      } else {
        // Wait for Swiper to load
        var check = setInterval(function() {
          if (typeof Swiper !== 'undefined') {
            clearInterval(check);
            self.initSwiper();
            self.filterSlidesByColor();
          }
        }, 50);
        // Give up after 5s
        setTimeout(function() { clearInterval(check); }, 5000);
      }

      this.bindEvents();

      // r154 (SJ 12.09): size memory — preselect the size this visitor chose last
      // in the same family (after bindEvents, so the programmatic click runs the
      // real pipeline). A ?variant= deep link always wins.
      this.applySizeMemory();
      // canonical, og:url and hreflang are the plain product URL anyway. The address bar keeps the size marker only
      // while no size is chosen (updateUrl): a reload or a shared link then opens the colour without a size, and once
      // a size is chosen it names that variant (22.10.2026)
      if (this._sizeOpen) this.updateUrl();

      // Re-apply variant on bfcache restore (back/forward navigation)
      window.addEventListener('pageshow', function(e) {
        if (e.persisted) {
          self.applyUrlVariant();
          self.currentVariant = self.getVariantFromOptions();
          self.filterSlidesByColor();
          self.updatePrice();
          self.updateSku();
          self.updateAddToCartButton();
          self.updateStockStatus();
          self.updateSizeAvailability();
        }
      });
    }

    /**
     * Initialize Swiper instances for main image and thumbnails.
     */
    initSwiper() {
      var self = this;
      var mainEl = this.section.querySelector('[data-product-main-swiper]');
      var thumbsEl = this.section.querySelector('[data-product-thumbs-swiper]');

      if (!mainEl) return;

      // The thumb rail is a plain flex row of 40px tiles in the media-band overlay,
      // so it needs no Swiper instance — only click wiring. Leaving thumbsSwiper null
      // also keeps the main swiper off the Thumbs module, which never coped with
      // slides being added and removed on every colour change.
      if (thumbsEl) {
        thumbsEl.querySelectorAll('.swiper-slide').forEach(function(thumb) {
          if (thumb.dataset.thumbBound) return;
          thumb.dataset.thumbBound = '1';
          thumb.addEventListener('click', function() {
            // 22.10.2026: the band slide of the same picture (data-media-id), not a position that a colour filter
            // or the loop of another swiper may have shifted
            var idx = self.bandIndexOf(thumb.dataset.mediaId);
            if (idx < 0) idx = parseInt(thumb.dataset.slideIndex, 10);
            if (!isNaN(idx) && idx > -1 && self.mainSwiper) {
              self.mainSwiper.slideTo(idx, 300);
            }
          });
        });
      }

      // slidesPerView 'auto' is what makes the band a filmstrip: each slide keeps its
      // natural width at full band height, so a portrait shot fills ~39% of the
      // viewport and the next image peeks in, as in the Figma PDP.
      this.mainSwiper = new Swiper(mainEl, {
        initialSlide: 0,
        spaceBetween: 0,
        slidesPerView: 'auto',
        // r37 SJ: the band must scroll horizontally like the native carousels —
        // trackpad/wheel input moves it (drag + touch already work via Swiper
        // defaults). forceToAxis keeps vertical page scroll untouched;
        // releaseOnEdges hands the gesture back to the page at both ends.
        mousewheel: { forceToAxis: true, releaseOnEdges: true },
        navigation: {
          nextEl: mainEl.querySelector('.swiper-button-next'),
          prevEl: mainEl.querySelector('.swiper-button-prev'),
        },
        // Swiper's a11y module overwrites the buttons' aria-label with its own
        // English defaults, so the German strings have to be passed in here.
        a11y: {
          prevSlideMessage: window.g26t('sections.collection_gallery.previous_image', 'Vorheriges Bild'),
          nextSlideMessage: window.g26t('sections.collection_gallery.next_image', 'Nächstes Bild'),
        },
        on: {
          slideChange: function() {
            // Update lightbox starting index when slide changes
            self._currentSlideIndex = this.activeIndex;
            self.updateBandCounter();
          },
          click: function(swiper, event) {
            // Open lightbox on click (but not on the overlay controls). 22.10.2026 (SJ: "a front may open a back, a
            // detail a front - no rhyme or reason"): on the CLICKED picture. The band is a filmstrip with two or three
            // pictures in view, and the lightbox used to open on the band's activeIndex - the leftmost one.
            if (event.target.closest('.g26-pdp__band-nav')) return;
            var slide = (event.target.closest && event.target.closest('.swiper-slide')) || swiper.clickedSlide;
            self.openLightbox(slide ? slide.dataset.mediaId : null);
          },
        },
      });

      this.updateBandCounter();
      this.updateThumbRail();
    }

    /**
     * Media-band counter ("2 von 6"). Counts the slides currently in the band,
     * which is the filtered set for the selected colour, not every product image.
     */
    updateBandCounter() {
      var el = this.section.querySelector('[data-band-counter]');
      if (!el) return;
      var total = this.mainSwiper && this.mainSwiper.slides ? this.mainSwiper.slides.length : 0;
      el.hidden = total < 2;
      if (total) el.textContent = window.g26tText('products.product.media.counter', '{{ current }} von {{ total }}', { current: this.mainSwiper.activeIndex + 1, total: total });
    }

    // The position of a picture (data-media-id) in the band as it stands - the selected colour's set. -1 when absent.
    bandIndexOf(mediaId) {
      if (!this.mainSwiper || !mediaId) return -1;
      var slides = Array.prototype.slice.call(this.mainSwiper.slides || []);
      for (var i = 0; i < slides.length; i++) if (slides[i].dataset.mediaId === String(mediaId)) return i;
      return -1;
    }

    /**
     * Cap the thumb rail at five tiles. Beyond that the last tile becomes a
     * "+N" counter that opens the lightbox, so the rail never outgrows the
     * corner of the media band.
     */
    updateThumbRail() {
      var rail = this.section.querySelector('[data-product-thumbs-swiper]');
      if (!rail) return;
      var more = this.section.querySelector('[data-thumbs-more]');
      var thumbs = Array.from(rail.querySelectorAll('.swiper-slide'));
      var MAX_TILES = 5;
      var shown = thumbs.length <= MAX_TILES ? thumbs.length : MAX_TILES - 1;

      thumbs.forEach(function(thumb, i) {
        thumb.classList.toggle('is-hidden', i >= shown);
      });

      if (!more) return;
      var hidden = thumbs.length - shown;
      more.hidden = hidden <= 0;
      more.textContent = hidden > 0 ? '+' + hidden : '';
    }

    /**
     * Apply variant from URL param (?variant=XXXXX) by activating the correct swatches/buttons.
     * This ensures JS state matches the URL even on bfcache restore or client-side navigation.
     */
    applyUrlVariant() {
      var url = new URL(window.location.href);
      var variantId = url.searchParams.get('variant');

      // Fall back to featured variant from data attribute if no URL param
      if (!variantId) {
        var section = this.section.closest('[data-featured-variant]');
        variantId = section ? section.dataset.featuredVariant : null;
      }
      if (!variantId) return;

      var variantIdNum = parseInt(variantId, 10);
      var variant = this.product.variants.find(function(v) {
        return v.id === variantIdNum;
      });
      if (!variant) return;

      // SJ 22.10.2026 (card a): only a plain ?variant= names the size (a cart or order line, the feed, a size-filtered
      // card). A colour link (size=open) and the featured fallback name the colour, and the size grid stays as the
      // server rendered it - no size - unless the product has a single size. Read from the address bar each time, so a
      // back/forward restore follows the state it was left in (updateUrl keeps size=open while no size is chosen).
      var sizeNamed = !!url.searchParams.get('variant') && url.searchParams.get('size') !== 'open';
      var leaveSize = !sizeNamed && this.sizeChoice();

      // Activate the correct option buttons/swatches for this variant
      var self = this;
      this.product.options.forEach(function(optName, idx) {
        var optValue = variant.options[idx];
        var group = self.section.querySelector('[data-option-index="' + idx + '"]');
        if (!group) return;
        if (leaveSize && group.dataset.optionName === 'Größe') return;

        if (group.tagName === 'SELECT') {
          group.value = optValue;
          return;
        }

        // Deactivate all, activate the matching one
        var buttons = group.querySelectorAll('[data-value]');
        var foundMatch = false;
        buttons.forEach(function(btn) {
          btn.classList.remove('active');
          if (btn.dataset.value === optValue) {
            btn.classList.add('active');
            foundMatch = true;
          }
        });

        // Update color label if this is the Farbe option
        if (foundMatch && optName === 'Farbe' && self.selectedColorLabel) {
          var activeSwatch = group.querySelector('.active');
          if (activeSwatch) {
            self.selectedColorLabel.textContent = activeSwatch.dataset.displayName || activeSwatch.title || '';
          }
        }
      });

      // r83 (adversarial review F1): run the real update pipeline instead of
      // hand-writing the input. onVariantChange() derives the variant from the
      // buttons just activated and updates price, CTA, stock line, size/colour
      // availability AND dispatches variant:change — so the back-in-stock block
      // reacts to a ?variant= deep link exactly like to a click. Before this,
      // a deep link to a sold-out variant kept the server-rendered state
      // (dead "Ausverkauft" button, no notify form).
      this.onVariantChange();
    }

    /**
     * r154 size memory: when the visitor chose a size in this family before and the
     * product carries it in stock (not struck, not hidden by a Schnitt), click that
     * button so price, stock line, URL and sticky bar follow. Never overrides a
     * ?variant= deep link. Shows a small „Ihre Größe 38 vorausgewählt" hint.
     */
    applySizeMemory() {
      var P = window.GoesslPersonal;
      if (!P || !P.sizes) return;
      // The pipeline above has already written ?variant= for the default pick, so the deep-link test reads the URL
      // the page was REQUESTED with, kept by the constructor. 22.10.2026: a colour link (size=open) chose the colour
      // only - the remembered size still applies there.
      if (this._urlVariant && !this._sizeOpen) return;
      var grid = this.section.querySelector('.goessl-product__size-grid[data-option-name="Größe"]');
      if (!grid || grid.hidden) return;
      var buttons = Array.from(grid.querySelectorAll('.goessl-product__size-btn')).filter(function(b) {
        return b.style.display !== 'none' && !b.classList.contains('unavailable');
      });
      if (!buttons.length) return;
      var fam = this.sizeFamily();
      var pick = P.sizes.match(fam, buttons.map(function(b) { return b.dataset.value; }));
      if (!pick) return;
      var btn = buttons.find(function(b) { return b.dataset.value === pick; });
      if (!btn) return;
      // r160 (Frau Adler 14.09): read the memory BEFORE the programmatic click. An apron size
      // derived from the remembered Dirndl size must stay derived - the click handler skips
      // remember() while _autoPick is set - so a later Dirndl size still leads the apron.
      var entry = P.sizes.get(fam) || {};
      if (!btn.classList.contains('active')) { this._autoPick = true; try { btn.click(); } finally { this._autoPick = false; } }
      var hint = this.section.querySelector('[data-size-hint]');
      if (hint) {
        hint.textContent = entry.derived
          ? window.g26tText('personal.size_preselected_derived', 'Größe {{ size }} vorausgewählt, entspricht Ihrer Größe {{ derived }}', { size: P.sizes.parse(pick).clean, derived: entry.derived })
          : window.g26t('personal.size_preselected', 'Ihre Größe {{ size }} vorausgewählt', { size: P.sizes.parse(pick).clean });
        hint.dataset.size = pick;
        hint.hidden = false;
      }
    }

    // r192: the size-memory family of this product - all its Größe values (sold out included) and the selected
    // Schnitt tell a men's shirt's collar sizes from the Konfektion sizes (GoesslPersonal.sizes.familyFor).
    // r197 (#353): the SKUs of the selected Schnitt's variants decide - its value is translated on /en ("Collar size").
    sizeFamily() {
      var p = this.product || {};
      var si = (p.options || []).indexOf('Größe');
      var values = si < 0 ? [] : (p.variants || []).map(function(v) { return v.options[si]; });
      var cutName = this.getSchnittOptionName();
      var cut = cutName ? this.getSelectedOptionValue(cutName) : null;
      var ci = cutName ? (p.options || []).indexOf(cutName) : -1;
      var skus = (p.variants || []).filter(function(v) { return ci < 0 || cut == null || v.options[ci] === cut; }).map(function(v) { return v.sku; });
      return window.GoesslPersonal.sizes.familyFor(p, values, cut, skus);
    }

    // r190b: the hint speaks for the preselected size only - another size hides it, that size again shows it
    syncSizeHint() {
      var hint = this.section.querySelector('[data-size-hint]');
      if (hint && hint.dataset.size) hint.hidden = this.getSelectedOptionValue('Größe') !== hint.dataset.size;
    }

    // ── No size until one is chosen (SJ 22.10.2026, card pdp-ainote-size-2210 = a) ──
    // The page picked a size on its own (the colour's first size that could be bought, e.g. rosa XXL) and it went
    // into the cart unseen. Now a product with more than one size opens without one (main-product.liquid size_open):
    // the button asks for it and leads to the sizes, the price covers the colour's sizes, and notify-me, the low-stock
    // line and a per-size pre-order note wait for the size.

    // the visible Größe grid (a single-size product carries a hidden one with its size selected)
    sizeGrid() {
      var grid = this.section.querySelector('.goessl-product__size-grid[data-option-name="Größe"]');
      return grid && !grid.hidden ? grid : null;
    }

    // the product asks for a size: its grid is shown with more than one size
    sizeChoice() {
      var grid = this.sizeGrid();
      return !!grid && grid.querySelectorAll('.goessl-product__size-btn').length > 1;
    }

    // no size chosen yet - none selected in the grid (a Schnitt change that hides the selected size clears it too)
    sizeMissing() {
      var grid = this.sizeGrid();
      return !!grid && !grid.querySelector('.goessl-product__size-btn.active');
    }

    // The buy button without a size: the message under the sizes, the first size that can be bought focused, the
    // grid in view - the pattern of the apron check (product-addons.js validate)
    askForSize() {
      var grid = this.sizeGrid();
      if (!grid) return;
      var alert = this.section.querySelector('[data-size-alert]');
      if (alert) alert.hidden = false;
      var shown = Array.from(grid.querySelectorAll('.goessl-product__size-btn')).filter(function(b) { return b.style.display !== 'none'; });
      var first = shown.find(function(b) { return !b.classList.contains('unavailable'); }) || shown[0];
      if (first) first.focus({ preventScroll: true });
      if (grid.scrollIntoView) grid.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    hideSizeAlert() {
      var alert = this.section.querySelector('[data-size-alert]');
      if (alert) alert.hidden = true;
    }

    // the variants of the selected colour (and Schnitt), every size
    colourVariants() {
      var p = this.product || {};
      var si = (p.options || []).indexOf('Größe');
      var self = this;
      var sel = (p.options || []).map(function(name) { return self.getSelectedOptionValue(name); });
      return (p.variants || []).filter(function(v) {
        return v.options.every(function(o, i) { return i === si || o === sel[i]; });
      });
    }

    // The price while no size is chosen, the rule of main-product.liquid (so_*): the colour's sizes that can be
    // bought, else all of them - one price or the range; a was-price only when every one of them has one, the lowest
    colourPrice() {
      var all = this.colourVariants();
      var pool = all.filter(function(v) { return v.available; });
      if (!pool.length) pool = all;
      if (!pool.length) return null;
      var prices = pool.map(function(v) { return v.price; });
      var compare = pool.every(function(v) { return v.compare_at_price > v.price; })
        ? Math.min.apply(null, pool.map(function(v) { return v.compare_at_price; })) : 0;
      return { min: Math.min.apply(null, prices), max: Math.max.apply(null, prices), compare: compare };
    }

    // the sticky bar's button says what the main button says
    syncStickyLabel(text) {
      var sticky = document.querySelector('[data-sticky-cart]');
      if (sticky) sticky.textContent = text;
    }

    /**
     * Read the selected value for a given option name.
     */
    getSelectedOptionValue(name) {
      var group = this.section.querySelector('[data-option-name="' + name + '"]');
      if (!group) return null;
      if (group.tagName === 'SELECT') return group.value;
      var active = group.querySelector('.active');
      return active ? active.dataset.value : null;
    }

    getOptionIndex(name) {
      var group = this.section.querySelector('[data-option-name="' + name + '"]');
      return group ? parseInt(group.dataset.optionIndex) : -1;
    }

    // The one way a colour swatch becomes the selected one - a click, or a hide pass
    // moving the selection off a colour it hides (06.10.2026). The caller runs onVariantChange.
    selectColorSwatch(btn) {
      var group = btn.closest('[data-option-name]') || this.section;
      group.querySelectorAll('.goessl-product__color-swatch').forEach(function(b) {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('active');
      btn.setAttribute('aria-pressed', 'true');
      if (this.selectedColorLabel) {
        this.selectedColorLabel.textContent = btn.dataset.displayName || btn.title || '';
      }
    }

    getSelectedColorCode() {
      // Liquid sets data-color-code on each swatch from the variant SKU (source of truth).
      // Reading from the active swatch avoids parsing the option text, which fails for
      // colors like "weiß 10" where "10" is a sub-shade, not the color_no (97).
      var active = this.section.querySelector('.goessl-product__color-swatch.active');
      if (active && active.dataset.colorCode) return active.dataset.colorCode;
      return extractColorCode(this.getSelectedOptionValue('Farbe'));
    }

    // Returns the name of the third option (e.g. "Schnitt") if present, else null.
    // Schnitt was added in 2026-05 for Lederhose products with multiple size systems
    // (Standardgröße 36–50 vs. Hosengröße 44–58); each Schnitt has a different
    // size scale, so size buttons must be filtered by current Schnitt selection.
    getSchnittOptionName() {
      if (!this.product || !this.product.options) return null;
      for (var i = 0; i < this.product.options.length; i++) {
        var name = this.product.options[i];
        if (name && name !== 'Farbe' && name !== 'Größe') return name;
      }
      return null;
    }

    bindEvents() {
      var self = this;

      document.addEventListener('variant:change', function() { self.syncSizeHint(); });

      // Color swatch clicks
      this.section.querySelectorAll('.goessl-product__color-swatch').forEach(function(btn) {
        btn.addEventListener('click', function() {
          self.selectColorSwatch(btn);
          self.onVariantChange('Farbe');
          // still no size (SJ 22.10.2026): the remembered one, if this colour has it
          if (self.sizeMissing()) self.applySizeMemory();
        });
      });

      // Size button clicks
      this.section.querySelectorAll('.goessl-product__size-btn').forEach(function(btn) {
        btn.addEventListener('click', function() {
          var group = btn.closest('[data-option-name]');
          group.querySelectorAll('.goessl-product__size-btn').forEach(function(b) {
            b.classList.remove('active');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('active');
          btn.setAttribute('aria-pressed', 'true');
          if (group.dataset.optionName === 'Größe') self.hideSizeAlert();
          self.onVariantChange('Größe');
          // r154: remember the choice for the next product of the same family
          // (r160: not for the automatic pre-selection, which would freeze a derived apron size)
          if (group.dataset.optionName === 'Größe' && window.GoesslPersonal && !self._autoPick) {
            window.GoesslPersonal.sizes.remember(self.sizeFamily(), btn.dataset.value);
          }
        });
      });

      // Fallback <select> elements
      this.section.querySelectorAll('.goessl-product__option-select').forEach(function(select) {
        select.addEventListener('change', function() {
          self.onVariantChange(this.dataset.optionName);
        });
      });

      // "+N" tile on the thumb rail → the lightbox on the first picture the rail does not show (22.10.2026; it
      // opened on whatever picture the band happened to start with)
      var moreBtn = this.section.querySelector('[data-thumbs-more]');
      if (moreBtn) {
        moreBtn.addEventListener('click', function() {
          var firstHidden = self.section.querySelector('[data-product-thumbs-swiper] .swiper-slide.is-hidden');
          self.openLightbox(firstHidden ? firstHidden.dataset.mediaId : null);
        });
      }

      // Lightbox events
      if (this.lightbox) {
        this.lightbox.querySelectorAll('[data-lightbox-close]').forEach(function(el) {
          el.addEventListener('click', self.closeLightbox.bind(self));
        });

        this._onKeydown = function(e) {
          if (self.lightbox.getAttribute('aria-hidden') !== 'false') return;
          if (e.key === 'Escape') self.closeLightbox();
        };
        document.addEventListener('keydown', this._onKeydown);
      }

      // Gutschein: recipient fields (only rendered on a gift-card product)
      this.initGiftCardRecipient();

      // Form submit
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    // ── Gutschein recipient form ────────────────────────────────────────────
    // Shopify's native recipient properties. While the box is unticked every
    // field is disabled, so nothing is sent and the buyer gets the Gutschein
    // by e-mail as before. The date window (today … today + 90 days) is set
    // from the visitor's own clock, because the page HTML may be cached.
    initGiftCardRecipient() {
      var wrap = this.form.querySelector('[data-gc-recipient]');
      this.gcRecipient = wrap || null;
      if (!wrap) return;

      var toggle = wrap.querySelector('[data-gcr-toggle]');
      var fields = wrap.querySelector('[data-gcr-fields]');
      var note = wrap.querySelector('[data-gcr-self-note]');
      var sendOn = wrap.querySelector('[data-gcr-send-on]');
      if (!toggle || !fields) return;
      this.form.noValidate = true;

      var pad = function(n) { return n < 10 ? '0' + n : String(n); };
      var iso = function(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); };
      if (sendOn) {
        var today = new Date();
        var last = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 90);
        sendOn.min = iso(today);
        sendOn.max = iso(last);
      }

      var self = this;
      var apply = function() {
        var on = toggle.checked;
        fields.hidden = !on;
        toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
        if (note) note.hidden = on;
        var inputs = fields.querySelectorAll('input, textarea');
        for (var i = 0; i < inputs.length; i++) inputs[i].disabled = !on;
        self.setGiftCardRecipientError('');
      };
      toggle.addEventListener('change', apply);
      apply();
    }

    setGiftCardRecipientError(message, field) {
      var wrap = this.gcRecipient;
      if (!wrap) return;
      var box = wrap.querySelector('[data-gcr-error]');
      var marked = wrap.querySelectorAll('[aria-invalid="true"]');
      for (var i = 0; i < marked.length; i++) marked[i].removeAttribute('aria-invalid');
      if (box) {
        box.textContent = message || '';
        box.hidden = !message;
      }
      if (message && field) {
        field.setAttribute('aria-invalid', 'true');
        try { field.focus(); } catch (e) { /* ignore */ }
      }
    }

    // Returns null when the box is unticked, false when a field is wrong (the
    // error is already on screen), otherwise the properties for the cart line.
    readGiftCardRecipient() {
      var wrap = this.gcRecipient;
      if (!wrap) return null;
      var toggle = wrap.querySelector('[data-gcr-toggle]');
      if (!toggle || !toggle.checked) return null;

      var email = wrap.querySelector('[data-gcr-email]');
      var sendOn = wrap.querySelector('[data-gcr-send-on]');
      var value = email ? email.value.trim() : '';
      if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        this.setGiftCardRecipientError(wrap.getAttribute('data-err-email'), email);
        return false;
      }
      if (sendOn && sendOn.value && ((sendOn.min && sendOn.value < sendOn.min) || (sendOn.max && sendOn.value > sendOn.max))) {
        this.setGiftCardRecipientError(wrap.getAttribute('data-err-date'), sendOn);
        return false;
      }

      var props = { '__shopify_send_gift_card_to_recipient': 'on', 'Recipient email': value };
      var inputs = wrap.querySelectorAll('[data-gcr-fields] input[name], [data-gcr-fields] textarea[name]');
      for (var i = 0; i < inputs.length; i++) {
        var m = /^properties\[(.+)\]$/.exec(inputs[i].name);
        if (!m || m[1] === 'Recipient email' || m[1] === '__shopify_offset') continue;
        var v = inputs[i].value.trim();
        if (v) props[m[1]] = v;
      }
      props['__shopify_offset'] = String(new Date().getTimezoneOffset());
      this.setGiftCardRecipientError('');
      return props;
    }

    sortSizeButtons() {
      var grids = this.section.querySelectorAll('.goessl-product__size-grid');
      grids.forEach(function(grid) {
        // Only the Größe grid needs the ladder sort — other option grids (e.g. gift-card
        // Nennwerte "€ 100,00") render in server order and localeCompare would scramble them
        if (grid.dataset.optionName && grid.dataset.optionName !== 'Größe') return;
        var buttons = Array.from(grid.querySelectorAll('.goessl-product__size-btn'));
        if (buttons.length <= 1) return;

        buttons.sort(function(a, b) {
          var aVal = a.dataset.value;
          var bVal = b.dataset.value;
          // Pure-integer sizes sort numerically — the SIZE_ORDER ladder only lists
          // even clothing sizes, so odd hat sizes (55/57/59/61) would fall to the end
          if (/^\d+$/.test(aVal) && /^\d+$/.test(bVal)) return parseInt(aVal, 10) - parseInt(bVal, 10);
          var aIdx = SIZE_ORDER.indexOf(aVal);
          var bIdx = SIZE_ORDER.indexOf(bVal);
          if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
          if (aIdx !== -1) return -1;
          if (bIdx !== -1) return 1;
          var aNum = parseFloat(aVal);
          var bNum = parseFloat(bVal);
          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
          return aVal.localeCompare(bVal);
        });

        buttons.forEach(function(btn) { grid.appendChild(btn); });
      });
    }

    /**
     * Hide color swatches that have no product images.
     */
    hideNoImageColors() {
      var swatches = this.section.querySelectorAll('.goessl-product__color-swatch');
      if (swatches.length <= 1) return;

      var mainSlides = this.section.querySelectorAll('[data-product-main-swiper] .swiper-slide');
      var hiddenCount = 0;

      var keep = this._urlColour;
      swatches.forEach(function(swatch) {
        var colorCode = swatch.dataset.colorCode;
        if (!colorCode) return;
        if (keep && swatch.dataset.value === keep) return;   // the colour the link named stays (22.10.2026)

        var hasImages = Array.from(mainSlides).some(function(slide) {
          return slide.dataset.colorCode === colorCode;
        });

        if (!hasImages) {
          swatch.style.display = 'none';
          hiddenCount++;
        }
      });

      if (hiddenCount > 0) {
        var activeSwatch = this.section.querySelector('.goessl-product__color-swatch.active');
        if (activeSwatch && activeSwatch.style.display === 'none') {
          var firstVisible = this.section.querySelector('.goessl-product__color-swatch:not([style*="display: none"])');
          if (firstVisible) {
            this.selectColorSwatch(firstVisible);
            this._colourMoved = true;   // the constructor lets the form follow
          }
        }
      }
    }

    hideOutOfStockColors() {
      var colorIdx = this.getOptionIndex('Farbe');
      if (colorIdx === -1) return;

      var swatches = this.section.querySelectorAll('.goessl-product__color-swatch');
      if (swatches.length <= 1) return;

      var variants = this.product.variants;
      var hiddenCount = 0;
      var keep = this._urlColour;

      swatches.forEach(function(swatch) {
        var colorValue = swatch.dataset.value;
        // the colour the link named stays, sold out or not: its sizes show struck and the notify-me form takes the
        // button's place, instead of the page jumping to another colour (22.10.2026)
        if (keep && colorValue === keep) return;
        var hasAvailable = variants.some(function(v) {
          return v.options[colorIdx] === colorValue && v.available;
        });
        if (!hasAvailable) {
          swatch.style.display = 'none';
          hiddenCount++;
        }
      });

      if (hiddenCount > 0) {
        var activeSwatch = this.section.querySelector('.goessl-product__color-swatch.active');
        if (activeSwatch && activeSwatch.style.display === 'none') {
          var firstVisible = this.section.querySelector('.goessl-product__color-swatch:not([style*="display: none"])');
          if (firstVisible) {
            this.selectColorSwatch(firstVisible);
            this._colourMoved = true;   // the constructor lets the form follow
          }
        }
      }
    }

    // A link can name a colour that has no swatch at all (no photo, no colour chip): then no swatch is active after
    // applyUrlVariant, and the first visible one takes over the way the hide passes move a hidden selection.
    keepColourSelected() {
      var swatches = Array.from(this.section.querySelectorAll('.goessl-product__color-swatch'));
      if (!swatches.length) return;
      if (swatches.some(function(s) { return s.classList.contains('active') && s.style.display !== 'none'; })) return;
      var first = swatches.find(function(s) { return s.style.display !== 'none'; });
      if (!first) return;
      this.selectColorSwatch(first);
      this._colourMoved = true;
    }

    getVariantFromOptions() {
      var options = [];
      var groups = {};

      this.section.querySelectorAll('[data-option-index]').forEach(function(el) {
        groups[el.dataset.optionIndex] = el;
      });

      for (var i = 0; i < this.product.options.length; i++) {
        var g = groups[i];
        if (!g) { options.push(''); continue; }
        if (g.tagName === 'SELECT') {
          options.push(g.value);
        } else {
          var active = g.querySelector('.active');
          options.push(active ? active.dataset.value : '');
        }
      }

      return this.product.variants.find(function(variant) {
        return variant.options.every(function(option, index) {
          return option === options[index];
        });
      });
    }

    onVariantChange(changedOptionName) {
      this.currentVariant = this.getVariantFromOptions();

      if (!this.currentVariant) {
        // r63 (SJ): the combination doesn't exist (colour with a partial size
        // range). The visual context must STILL follow the click — gallery and
        // size strikes always reflect the chosen colour. r68 (SJ): the chosen
        // size SURVIVES the colour switch — it renders struck+selected and the
        // missing-combo path below (stock line + notify-me) speaks for it.
        // Only a size hidden by a Schnitt change (different size system, the
        // button is display:none) still gets deselected — an invisible
        // selection would drive price/BIS with a size the grid doesn't show.
        if (changedOptionName === 'Farbe') {
          this.filterSlidesByColor();
          this.updateSizeAvailability();
          var activeSize = this.section.querySelector('.goessl-product__size-btn.active');
          if (activeSize && activeSize.style.display === 'none') {
            activeSize.classList.remove('active');
            activeSize.setAttribute('aria-pressed', 'false');
          }
        }
        if (changedOptionName !== 'Farbe' && changedOptionName !== 'Größe') {
          this.updateSizeAvailability();
        }
        // SJ 22.10.2026: no size chosen yet is not a missing combination - the button asks for the size (not
        // "Nicht verfügbar"), the price covers the colour's sizes, and the address bar names the colour only
        var noSize = this.sizeMissing();
        this.updateColorAvailability();
        this.updateAddToCartButton(false);
        // r68: clear the stale id — the store line and the availability modal
        // read this input and must not answer for the PREVIOUS variant
        this.variantIdInput.value = '';
        // r66 (SJ): the rest of the page must not go stale either — stock line
        // shows "Nicht verfügbar" (updateStockStatus handles null), and the
        // BIS block gets a variant:change with variant:null + the selection, so
        // notify-me works even for a colour/size combo that has no variant yet
        // (recorded as demand for manual follow-up in the partner dash).
        this.updateStockStatus();
        if (noSize) {
          this.updatePrice();
          this.updateUrl(changedOptionName === 'Farbe');
          document.querySelectorAll('[data-sticky-size].active').forEach(function(b) { b.classList.remove('active'); });
        }
        var sizeBtnNV = this.section.querySelector('.goessl-product__size-btn.active');
        document.dispatchEvent(new CustomEvent('variant:change', {
          detail: {
            variant: null,
            missing: !noSize,
            sizeOpen: noSize,
            options: null,
            colorValue: this.getSelectedOptionValue('Farbe') || '',
            sizeValue: sizeBtnNV ? sizeBtnNV.dataset.value : ''
          }
        }));
        return;
      }

      this.variantIdInput.value = this.currentVariant.id;
      this.hideSizeAlert();
      this.updatePrice();
      this.updateSku();
      this.updateAddToCartButton();
      this.updateStockStatus();
      this.updateUrl();

      if (changedOptionName === 'Farbe') {
        this.filterSlidesByColor();
        this.updateVariantImage();
        this.updateSizeAvailability();
        this.updateColorAvailability();
      }

      if (changedOptionName === 'Größe') {
        this.updateVariantImage();
        // r63: with a size chosen, grey out the colours that don't carry it
        this.updateColorAvailability();
      }

      // Schnitt or any other 3rd option: re-filter visible sizes since each
      // Schnitt has its own size scale (Lederhose: Standardgröße vs Hosengröße),
      // and re-grey colours not available in the newly-selected Schnitt (#523).
      if (changedOptionName !== 'Farbe' && changedOptionName !== 'Größe') {
        this.updateSizeAvailability();
        this.updateColorAvailability();
      }

      // Dispatch event for sticky bar and other listeners
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: this.currentVariant,
          price: this.currentVariant.price,
          options: this.currentVariant.options
        }
      }));
    }

    /**
     * Filter Swiper slides to show only images for the selected color.
     * Physically swaps slides in/out of the wrapper and recreates Swiper.
     */
    /**
     * The one colour rule of the band, the thumbs and the lightbox (team lead 22.10.2026: they must not drift apart),
     * the same main-product.liquid paints server-side (img_offs): a colour with photos of its own shows them plus the
     * uncoded ones; a colour without (hex-chip colourways, r63) shows every photo, so the band is never blank. Before,
     * an uncoded photo counted as "own", so a colour without own photos showed only the uncoded ones here while the
     * server painted all of them.
     */
    slideFilter(slides, colorCode) {
      var own = !!colorCode && slides.some(function(s) { return s.dataset.colorCode === colorCode; });
      return function(slide) {
        return !own || !slide.dataset.colorCode || slide.dataset.colorCode === colorCode;
      };
    }

    filterSlidesByColor() {
      var colorCode = this.getSelectedColorCode();
      if (!colorCode) return;

      var mainWrapper = this.section.querySelector('[data-product-main-swiper] .swiper-wrapper');
      var thumbsWrapper = this.section.querySelector('[data-product-thumbs-swiper] .swiper-wrapper');
      if (!mainWrapper) return;

      // Cache all slides on first call
      if (!this._allMainSlides) {
        this._allMainSlides = Array.from(mainWrapper.querySelectorAll('.swiper-slide'));
        this._allThumbSlides = thumbsWrapper ? Array.from(thumbsWrapper.querySelectorAll('.swiper-slide')) : [];
        // the server painted only the selected colour (--off on the rest, main-product.liquid); from here this filter owns the set
        this._allMainSlides.forEach(function(s) { s.classList.remove('g26-pdp__slide--off'); });
        this._allThumbSlides.forEach(function(s) { s.classList.remove('g26-pdp__thumb--off'); });
      }

      // Destroy existing swipers before modifying DOM
      if (this.mainSwiper) { this.mainSwiper.destroy(true, true); this.mainSwiper = null; }
      if (this.thumbsSwiper) { this.thumbsSwiper.destroy(true, true); this.thumbsSwiper = null; }

      // Remove all children from wrappers
      while (mainWrapper.firstChild) mainWrapper.removeChild(mainWrapper.firstChild);
      if (thumbsWrapper) while (thumbsWrapper.firstChild) thumbsWrapper.removeChild(thumbsWrapper.firstChild);

      // Append only the slides the colour rule shows (slideFilter - the rule main-product.liquid paints server-side)
      var show = this.slideFilter(this._allMainSlides, colorCode);
      this._allMainSlides.forEach(function(slide) {
        if (show(slide)) mainWrapper.appendChild(slide);
      });
      this._allThumbSlides.forEach(function(slide) {
        if (show(slide)) thumbsWrapper.appendChild(slide);
      });

      // Update thumb data-slide-index to match new sequential order
      if (thumbsWrapper) {
        thumbsWrapper.querySelectorAll('.swiper-slide').forEach(function(thumb, i) {
          thumb.dataset.slideIndex = i;
        });
      }

      // Recreate swipers with filtered slides
      this.initSwiper();
    }

    updateSizeAvailability() {
      var colorIdx = this.getOptionIndex('Farbe');
      var sizeIdx = this.getOptionIndex('Größe');
      if (colorIdx === -1 || sizeIdx === -1) return;

      var selectedColor = this.getSelectedOptionValue('Farbe');
      if (!selectedColor) return;

      var schnittName = this.getSchnittOptionName();
      var schnittIdx = schnittName ? this.getOptionIndex(schnittName) : -1;
      var selectedSchnitt = schnittName ? this.getSelectedOptionValue(schnittName) : null;

      var sizeGrid = this.section.querySelector('.goessl-product__size-grid[data-option-name="Größe"]');
      if (!sizeGrid) return;

      var variants = this.product.variants;
      var stickyBar = document.querySelector('[data-sticky-bar]');
      var firstAvailable = null;
      var hasActiveVisible = false;

      sizeGrid.querySelectorAll('.goessl-product__size-btn').forEach(function(btn) {
        var sizeValue = btn.dataset.value;
        var variant = variants.find(function(v) {
          if (v.options[colorIdx] !== selectedColor) return false;
          if (v.options[sizeIdx] !== sizeValue) return false;
          if (schnittIdx !== -1 && v.options[schnittIdx] !== selectedSchnitt) return false;
          return true;
        });

        // Mirror visibility on sticky bar size button
        var stickyBtn = stickyBar ? stickyBar.querySelector('[data-sticky-size="' + CSS.escape(sizeValue) + '"]') : null;

        if (!variant && schnittIdx !== -1) {
          // Size doesn't exist for this color+schnitt combo (different size scale).
          // Hide entirely rather than gray out — it's a different size system.
          btn.style.display = 'none';
          setUnavailable(btn, null);
          if (stickyBtn) stickyBtn.style.display = 'none';
        } else if (!variant) {
          // r63 (SJ): the colour simply doesn't come in this size (partial size
          // ranges, e.g. absorbed till colourways). Same size system → strike it.
          // Falling through to "available" here is what showed phantom sizes.
          btn.style.display = '';
          setUnavailable(btn, 'unavailable');
          if (stickyBtn) {
            stickyBtn.style.display = '';
            setUnavailable(stickyBtn, 'unavailable');
          }
        } else if (variant && !variant.available) {
          btn.style.display = '';
          setUnavailable(btn, 'sold_out');
          if (stickyBtn) {
            stickyBtn.style.display = '';
            setUnavailable(stickyBtn, 'sold_out');
          }
        } else {
          btn.style.display = '';
          setUnavailable(btn, null);
          if (stickyBtn) {
            stickyBtn.style.display = '';
            setUnavailable(stickyBtn, null);
          }
          if (variant && variant.available) {
            if (!firstAvailable) firstAvailable = btn;
            if (btn.classList.contains('active')) hasActiveVisible = true;
          }
        }
      });

      // If a Schnitt change hid the previously-active size (different size
      // system, button display:none), that selection goes. r68 (SJ): a size
      // that is merely STRUCK (colour gap, same size system) keeps the
      // selection — the missing-combo path renders it struck+selected with the
      // notify-me box. SJ 22.10.2026 (card a): no size of the page's own
      // choosing - this used to jump to the first available size of the new
      // scale, and picked one on the first load of every Schnitt product
      // (Lederhosen opened on 50); the visitor or the remembered size chooses.
      if (schnittIdx !== -1 && !hasActiveVisible) {
        var prev = sizeGrid.querySelector('.goessl-product__size-btn.active');
        if (prev && prev.style.display === 'none') {
          prev.classList.remove('active');
          prev.setAttribute('aria-pressed', 'false');
          // Defer variant refresh so the calling onVariantChange can finish first
          var self = this;
          setTimeout(function() {
            self.onVariantChange('Größe');
            if (self.sizeMissing()) self.applySizeMemory();
          }, 0);
        }
      }
    }

    /**
     * #523 — grey out color swatches not available in the SELECTED STYLE (Schnitt).
     * Multi-cut products (e.g. a Schnitt option alongside Farbe/Größe) can offer a
     * colour in one cut but not another. When a Schnitt is selected, any colour with
     * no available variant in that Schnitt gets `.unavailable` (greyed + strike),
     * mirroring updateSizeAvailability for sizes. No-op for single-Schnitt products
     * (hideOutOfStockColors already hides fully-OOS colours there). Never hides — only
     * greys, so the customer still sees the colour exists.
     */
    updateColorAvailability() {
      // r63 (SJ): greying is now driven by the WHOLE selection, not only Schnitt —
      // with a size selected, every colour that has no available variant in that
      // size greys out (mirror of the size grid). Greyed swatches stay clickable:
      // the grey is information, not a lock, and clicking still switches photos.
      var colorIdx = this.getOptionIndex('Farbe');
      if (colorIdx === -1) return;

      var schnittName = this.getSchnittOptionName();
      var schnittIdx = schnittName ? this.getOptionIndex(schnittName) : -1;
      var selectedSchnitt = schnittName ? this.getSelectedOptionValue(schnittName) : null;

      var sizeIdx = this.getOptionIndex('Größe');
      var activeSizeBtn = this.section.querySelector('.goessl-product__size-btn.active');
      var selectedSize = (sizeIdx !== -1 && activeSizeBtn) ? activeSizeBtn.dataset.value : null;

      if (!selectedSchnitt && !selectedSize) {
        // nothing selected to gate on → clear any stale greys
        this.section.querySelectorAll('.goessl-product__color-swatch.unavailable')
          .forEach(function(s) { setUnavailable(s, null); });
        return;
      }

      var variants = this.product.variants;
      var swatches = this.section.querySelectorAll('.goessl-product__color-swatch');
      if (swatches.length <= 1) return;

      var self = this;
      var activeWentUnavailableBySchnitt = false;
      var firstAvailable = null;

      swatches.forEach(function(swatch) {
        // Skip swatches already hidden (no image / fully OOS) — leave them hidden.
        if (swatch.style.display === 'none') return;
        var colorValue = swatch.dataset.value;
        if (!colorValue) return;
        var matching = variants.filter(function(v) {
          if (v.options[colorIdx] !== colorValue) return false;
          if (selectedSchnitt && schnittIdx !== -1 && v.options[schnittIdx] !== selectedSchnitt) return false;
          if (selectedSize && v.options[sizeIdx] !== selectedSize) return false;
          return true;
        });
        var availableForSelection = matching.some(function(v) { return v.available; });
        if (availableForSelection) {
          setUnavailable(swatch, null);
          if (!firstAvailable) firstAvailable = swatch;
        } else {
          setUnavailable(swatch, matching.length ? 'sold_out' : 'unavailable');
          if (swatch.classList.contains('active') && selectedSchnitt) {
            // Auto-switch remains a Schnitt-only behaviour (#523): check the
            // Schnitt dimension alone so a size-driven grey never yanks the
            // customer off the colour they are looking at.
            var availableInSchnitt = variants.some(function(v) {
              return v.options[colorIdx] === colorValue
                && v.options[schnittIdx] === selectedSchnitt
                && v.available;
            });
            if (!availableInSchnitt) activeWentUnavailableBySchnitt = true;
          }
        }
      });

      // If the active colour is unavailable in this Schnitt, switch to the first
      // available colour so the PDP never sits on an all-sold-out colour after a
      // Schnitt change (mirrors the size-grid behaviour).
      if (activeWentUnavailableBySchnitt && firstAvailable) {
        var active = this.section.querySelector('.goessl-product__color-swatch.active');
        if (active) active.classList.remove('active');
        firstAvailable.classList.add('active');
        if (this.selectedColorLabel) {
          this.selectedColorLabel.textContent = firstAvailable.dataset.displayName || firstAvailable.title || '';
        }
        setTimeout(function() { self.onVariantChange('Farbe'); }, 0);
      }
    }

    updatePrice() {
      if (!this.priceContainer) return;
      // the selected variant's price; without a size (SJ 22.10.2026) the colour's sizes - one price or the range
      var v = this.currentVariant;
      var p = v ? { min: v.price, max: v.price, compare: v.compare_at_price || 0 } : (this.sizeMissing() ? this.colourPrice() : null);
      if (!p) return;

      var taxOld = this.priceContainer.querySelector('.goessl-product__tax-inline');
      var taxText = taxOld ? taxOld.textContent : '';
      while (this.priceContainer.firstChild) {
        this.priceContainer.removeChild(this.priceContainer.firstChild);
      }

      var sticky = document.querySelector('[data-sticky-price]');
      if (p.max > p.min) {
        var rangeSpan = document.createElement('span');
        rangeSpan.className = 'goessl-product__price-regular';
        rangeSpan.textContent = this.formatMoney(p.min) + '\u00a0\u2013\u00a0' + this.formatMoney(p.max);
        this.priceContainer.appendChild(rangeSpan);
        if (sticky) sticky.textContent = rangeSpan.textContent;
      } else if (p.compare > p.min) {
        var compareSpan = document.createElement('span');
        compareSpan.className = 'goessl-product__price-compare';
        compareSpan.textContent = this.formatMoney(p.compare);

        var saleSpan = document.createElement('span');
        saleSpan.className = 'goessl-product__price-sale';
        saleSpan.textContent = this.formatMoney(p.min);

        var discountPct = Math.round((p.compare - p.min) * 100 / p.compare);
        var badgeSpan = document.createElement('span');
        badgeSpan.className = 'goessl-product__discount-badge';
        badgeSpan.textContent = '−' + discountPct + '%';

        // Order matches the Liquid render and the design: was-price, discount, now-price.
        this.priceContainer.appendChild(compareSpan);
        this.priceContainer.appendChild(badgeSpan);
        this.priceContainer.appendChild(saleSpan);
      } else {
        var priceSpan = document.createElement('span');
        priceSpan.className = 'goessl-product__price-regular';
        priceSpan.textContent = this.formatMoney(p.min);
        this.priceContainer.appendChild(priceSpan);
      }
      if (sticky && p.max <= p.min) sticky.textContent = this.formatMoney(p.min);

      // Re-add inline tax note - the server's wording (a B2B account reads "zzgl. MwSt.")
      var taxSpan = document.createElement('span');
      taxSpan.className = 'goessl-product__tax-inline';
      taxSpan.textContent = taxText || window.g26tText('products.product.include_taxes', 'inkl. MwSt.');
      this.priceContainer.appendChild(taxSpan);
    }

    updateSku() {
      if (!this.skuElement) return;
      this.skuElement.textContent = this.currentVariant ? this.currentVariant.sku || '' : '';
    }

    updateAddToCartButton(variantExists) {
      if (variantExists === undefined) variantExists = true;
      if (!this.addToCartBtn) return;

      if (!variantExists || !this.currentVariant) {
        if (this.sizeMissing()) {
          // SJ 22.10.2026: no size yet - the button asks for one and leads to the sizes (onSubmit -> askForSize)
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.textContent = window.g26tText('products.product.choose_size', 'Größe wählen');
        } else {
          this.addToCartBtn.disabled = true;
          this.addToCartBtn.textContent = window.theme && window.theme.strings ? window.theme.strings.unavailable : window.g26tText('products.product.unavailable', 'Nicht verfügbar');
        }
        this.syncStickyLabel(this.addToCartBtn.textContent);
        this.syncCtaWithBis();
        this.syncPreorderUi();
        return;
      }

      if (this.currentVariant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = this.isPreorderVariant(this.currentVariant)
          ? window.g26tText('products.product.preorder', 'Vorbestellen')
          : this.addToCartLabel();
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = window.theme && window.theme.strings ? window.theme.strings.soldOut : window.g26tText('products.product.sold_out', 'Ausverkauft');
      }
      this.syncStickyLabel(this.addToCartBtn.textContent);
      this.syncCtaWithBis();
      this.syncPreorderUi();
    }

    addToCartLabel() {
      return window.theme && window.theme.strings ? window.theme.strings.addToCart : window.g26t('products.product.add_to_cart', 'In den Warenkorb');
    }

    // r187 (Birgit 01.10.2026: delivered pre-order pieces still read "Vorbestellung"): inside a
    // pre-order product a size WITH online stock sells normally - no selling plan, normal button,
    // no delivery date; a size without stock stays a pre-order. Stock = the tracked quantity in
    // [data-product-inventory] (Liquid counts only locations that fulfil online orders; a placed
    // pre-order makes it negative) - the rule of snippets/preorder-stock.liquid.
    isPreorderVariant(variant) {
      if (!this.preorderPlanId) return false;
      if (!variant) return true;
      var q = this.inventory ? this.inventory[variant.id] : null;
      return !(typeof q === 'number' && q > 0);
    }

    // Puts the pre-order furniture in step with the selected size: the hidden selling_plan input
    // (disabled = not posted by the no-JS form), the date + note box, the sticky-bar label. Runs on
    // load too, so a ?variant= link lands in the right state. A colour/size combination without a
    // variant keeps the box as it was - there is no size to answer for.
    syncPreorderUi() {
      if (!this.preorderPlanId) return;
      var v = this.currentVariant;
      var pre, noteFrom;
      if (v) {
        pre = this.isPreorderVariant(v);
        noteFrom = v;
      } else if (this.sizeMissing()) {
        // SJ 22.10.2026: no size yet - the note speaks for the colour only when every size of it is a pre-order
        // (main-product.liquid colour_preorder); nothing is posted without a size
        var self = this;
        var pool = this.colourVariants();
        pre = pool.length > 0 && pool.every(function(x) { return self.isPreorderVariant(x); });
        noteFrom = pool[0];
        if (this.sellingPlanInput) this.sellingPlanInput.disabled = true;
      } else {
        return;   // a colour without the chosen size keeps the box as it was - there is no size to answer for
      }
      if (v && this.sellingPlanInput) this.sellingPlanInput.disabled = !pre;
      if (this.preorderBox) {
        this.preorderBox.hidden = !pre;
        this.preorderBox.classList.toggle('g26-pdp__preorder--no-size', !v);   // the reserve link waits for a size
        var date = this.preorderBox.querySelector('[data-preorder-date]');
        if (date) {
          var t = (pre && noteFrom && this.preorderNotes[String(noteFrom.id)]) || '';
          date.textContent = t;
          date.hidden = !t;
        }
      }
      // the sticky bar's label follows the main button (updateAddToCartButton, which runs this)
    }

    // r78b (SJ): no grey "Ausverkauft" block — the notify-me form replaces the CTA.
    // Deferred a tick so the BIS variant:change listener settles its visibility first;
    // rule: CTA hidden exactly when the BIS box is visible (covers sold-out and
    // missing-combo-with-size, and restores the button when a buyable size is picked).
    syncCtaWithBis() {
      var btn = this.addToCartBtn;
      if (!btn) return;
      setTimeout(function () {
        var bis = document.querySelector('[data-bis]');
        btn.hidden = !!(bis && !bis.hidden);
      }, 0);
    }

    // Low stock (SJ 22.10.2026: she bought the last green XL with no warning). r68 dropped the element this used to
    // write ([data-stock-status]) and it returned early ever since. Now the store row's low-stock line: shown only while
    // the selected size has 1-4 pieces left across every location that ships online orders - "Nur noch 1 verfügbar",
    // else "Auf Lager, weniger als 5 verfügbar" - and hidden for more, for a size that is sold out or missing, and for
    // a pre-order size (it sells beyond its stock; snippets/low-stock.liquid lists only deny-policy sizes anyway).
    updateStockStatus() {
      var box = this.lowStock;
      if (!box) return;
      var v = this.currentVariant;
      var q = v && v.available && !this.isPreorderVariant(v) ? Number(this.lowStockMap[String(v.id)]) : 0;
      if (!(q >= 1 && q <= 4)) { box.hidden = true; return; }
      var text = box.querySelector('[data-low-stock-text]') || box;
      text.textContent = q === 1
        ? window.g26tText('products.product.stock_last_one', 'Nur noch 1 verfügbar')
        : window.g26tText('products.product.stock_low', 'Auf Lager, weniger als {{ count }} verfügbar', { count: 5 });
      box.hidden = false;
    }

    // colourChanged: the visitor picked another colour while no size is chosen
    updateUrl(colourChanged) {
      if (!window.history.replaceState) return;
      var url = new URL(window.location.href);
      if (this.currentVariant) {
        url.searchParams.set('variant', this.currentVariant.id);
        // the address bar names the variant on the page - a reload or a shared link opens exactly it (22.10.2026)
        if (url.searchParams.get('size') === 'open') url.searchParams.delete('size');
      } else if (this.sizeMissing()) {
        // SJ 22.10.2026: no size chosen - the colour only, as a colour link names it (snippets/product-colour-url.liquid:
        // the colour's first variant that can be bought, else its first, and size=open), so a reload or a shared link
        // opens the colour without inventing a size. A plain visit keeps its plain address until the colour changes.
        if (!colourChanged && !url.searchParams.get('variant')) return;
        var pool = this.colourVariants();
        var named = url.searchParams.get('variant');
        // a colour link that already names this colour stays as it came
        if (url.searchParams.get('size') === 'open' && pool.some(function(x) { return String(x.id) === named; })) return;
        var pick = pool.find(function(x) { return x.available; }) || pool[0];
        if (!pick) return;
        url.searchParams.set('variant', pick.id);
        url.searchParams.set('size', 'open');
      } else {
        return;
      }
      window.history.replaceState({}, '', url.toString());
    }

    updateVariantImage() {
      // After filterSlidesByColor, the gallery only contains the current color's images.
      // Just slide to index 0 (the front image for this color).
      if (this.mainSwiper) {
        this.mainSwiper.slideTo(0, 0);
      }
    }

    async onSubmit(e) {
      e.preventDefault();

      // SJ 22.10.2026: no size chosen - the button leads to the sizes instead of guessing one
      if (!this.currentVariant && this.sizeMissing()) { this.askForSize(); return; }
      if (!this.currentVariant || !this.currentVariant.available) return;

      // r190: a ticked add-on (apron) needs its size before anything goes into the cart
      var addonsBlock = this.section.querySelector('[data-addons]');
      if (addonsBlock && window.GoesslAddons && !window.GoesslAddons.validate(addonsBlock)) return;

      // a11y #902: the button that sent the piece to the cart (this one or the sticky bar's) gets focus back when
      // the cart drawer closes - read it now, disabling the button below drops focus to the page
      var opener = document.activeElement && document.activeElement !== document.body ? document.activeElement : this.addToCartBtn;
      this.addToCartBtn.disabled = true;
      var originalText = this.addToCartBtn.textContent;
      this.addToCartBtn.textContent = window.theme && window.theme.strings ? window.theme.strings.loading : window.g26tText('products.product.adding', 'Wird hinzugefügt...');

      // Hidden line-item property carrying the Intex article number for
      // store-team lookup in Shopify Admin (Steyr/Bergheim search Intex by
      // this). Sourced from the `Artikelnr:<value>` tag mirrored by fn39.
      // _-prefix = admin-only (hidden from customer-facing cart/checkout).
      var lineItem = { id: this.currentVariant.id, quantity: 1 };
      // r187: only a size without stock carries the plan; a size with stock is an ordinary sale
      if (this.isPreorderVariant(this.currentVariant)) lineItem.selling_plan = Number(this.preorderPlanId);
      try {
        var tags = (this.product && this.product.tags) || [];
        for (var t = 0; t < tags.length; t++) {
          if (typeof tags[t] === 'string' && tags[t].indexOf('Artikelnr:') === 0) {
            var v = tags[t].slice(10).trim();
            if (v) lineItem.properties = { '_Artikel-Nr': v };
            break;
          }
        }
      } catch (e) { /* ignore */ }

      // Gutschein: recipient properties (null = box unticked, false = a field is wrong)
      var gcProps = this.readGiftCardRecipient();
      if (gcProps === false) {
        this.addToCartBtn.textContent = originalText;
        this.addToCartBtn.disabled = false;
        return;
      }
      if (gcProps) {
        lineItem.properties = lineItem.properties || {};
        for (var gk in gcProps) {
          if (Object.prototype.hasOwnProperty.call(gcProps, gk)) lineItem.properties[gk] = gcProps[gk];
        }
      }

      try {
        var response = await fetch(window.g26url('cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(lineItem)
        });

        if (!response.ok) {
          if (gcProps && this.gcRecipient) {
            // Shopify names the field (email / send_on / message / name); its own sentences are
            // half English ("Send on muss innerhalb von 90 Tagen ...") and change shape with the
            // payload, so the page answers in its own words and marks the field.
            var gcWrap = this.gcRecipient;
            var gcMessage = gcWrap.getAttribute('data-err-generic');
            var gcField = null;
            try {
              var gcBody = await response.json();
              var gcErrors = (gcBody && (gcBody.errors || gcBody.description)) || {};
              if (gcErrors && typeof gcErrors === 'object') {
                if (gcErrors.email) {
                  gcMessage = gcWrap.getAttribute('data-err-email');
                  gcField = gcWrap.querySelector('[data-gcr-email]');
                } else if (gcErrors.send_on) {
                  gcMessage = gcWrap.getAttribute('data-err-date');
                  gcField = gcWrap.querySelector('[data-gcr-send-on]');
                }
              }
            } catch (parseError) { /* keep the generic message */ }
            this.setGiftCardRecipientError(gcMessage, gcField);
          }
          throw new Error('Failed to add to cart');
        }

        // r190: the ticked add-ons follow the main piece in one more request. The main piece is in the cart
        // either way; if the add-ons fail, the drawer says so and they stay ticked for another try.
        var addonItems = addonsBlock && window.GoesslAddons ? window.GoesslAddons.items(addonsBlock) : [];
        if (addonItems.length) {
          try {
            var addonResponse = await fetch(window.g26url('cart/add.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: addonItems })
            });
            if (!addonResponse.ok) throw new Error('add-ons ' + addonResponse.status);
            window.GoesslAddons.reset(addonsBlock);
          } catch (addonError) {
            console.error('Error adding add-ons:', addonError);
            document.dispatchEvent(new CustomEvent('cart:notice', { detail: { message: addonsBlock.getAttribute('data-addons-failed') } }));
          }
        }

        // r188: a size sold from stock never takes more units than it has - a second click moves the
        // extra piece onto a pre-order line (assets/preorder-cart.js), the drawer says so
        // (r190: also when only an add-on is a pre-order piece)
        if ((this.preorderPlanId || addonItems.length) && window.g26preorder && window.g26preorder.capCart) await window.g26preorder.capCart();

        await this.updateCartCount();

        this.addToCartBtn.textContent = window.theme && window.theme.strings ? window.theme.strings.addedToCart : window.g26tText('products.product.added_short', 'Hinzugefügt!');

        var cartDrawer = document.querySelector('[data-cart-drawer]');
        if (cartDrawer) {
          document.dispatchEvent(new CustomEvent('cart:open', { detail: { opener: opener } }));
        }

        var self = this;
        setTimeout(function() {
          self.addToCartBtn.textContent = originalText;
          self.addToCartBtn.disabled = false;
        }, 2000);

      } catch (error) {
        console.error('Error adding to cart:', error);
        this.addToCartBtn.textContent = window.theme && window.theme.strings ? window.theme.strings.error : window.g26tText('general.accessibility.error', 'Fehler');
        var self2 = this;
        setTimeout(function() {
          self2.addToCartBtn.textContent = originalText;
          self2.addToCartBtn.disabled = false;
        }, 2000);
      }
    }

    async updateCartCount() {
      try {
        var response = await fetch(window.g26url('cart.js'));
        var cart = await response.json();

        var cartCounts = document.querySelectorAll('[data-cart-count]');
        cartCounts.forEach(function(el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });

        var liveRegion = document.querySelector('[data-cart-live-region]');
        if (liveRegion) {
          liveRegion.textContent = window.g26tText('cart.general.live_region', '{{ count }} Artikel im Warenkorb', { count: cart.item_count });
        }
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    }

    initLightboxSwiper(count) {
      var self = this;
      var swiperEl = this.lightbox.querySelector('[data-lightbox-swiper]');
      if (!swiperEl) return;

      this.lightboxSwiper = new Swiper(swiperEl, {
        spaceBetween: 0,
        slidesPerView: 1,
        // 22.10.2026: Swiper 11 cannot loop two pictures (it warns and keeps the order it can) - loop from three
        loop: count >= 3,
        navigation: {
          nextEl: swiperEl.querySelector('.swiper-button-next'),
          prevEl: swiperEl.querySelector('.swiper-button-prev'),
        },
        keyboard: { enabled: true, onlyInViewport: false },
        // Swiper's built-in Zoom: native pinch on mobile, double-tap toggle,
        // pan when zoomed. Single-click toggle for desktop is wired below.
        zoom: {
          maxRatio: 2.5,
          minRatio: 1,
          toggle: true,
        },
        on: {
          slideChange: function() {
            // zoom.out() with no active gesture crashes inside Swiper (fires on the
            // construction-time slideChange) and the whole open aborts — only reset
            // when actually zoomed in, and never let it kill the open (SJ 26.08).
            if (this.zoom && this.zoom.scale && this.zoom.scale !== 1) {
              try { this.zoom.out(); } catch (e) { /* zoomed-out already */ }
            }
            self.updateLightboxCounter();
          },
        },
      });

      // Desktop single-click toggle. Touch devices keep Swiper's native
      // pinch + double-tap; this only adds the click affordance for mice.
      if (window.matchMedia('(pointer: fine)').matches) {
        swiperEl.addEventListener('click', function(e) {
          if (e.target.closest('.swiper-button-prev')) return;
          if (e.target.closest('.swiper-button-next')) return;
          if (e.target.closest('[data-lightbox-close]')) return;
          if (!e.target.closest('.goessl-lightbox__image')) return;
          if (!self.lightboxSwiper || !self.lightboxSwiper.zoom) return;
          self.lightboxSwiper.zoom.toggle();
        });
      }
    }

    /**
     * 22.10.2026 (SJ: "a front may open a back, a detail a front - no rhyme or reason"). The lightbox opens on the
     * picture that was clicked and holds exactly the pictures the band holds, in the band's order: both are matched
     * by data-media-id, never by position. Before, it filtered its own copy by colour (no fallback for a colour
     * without photos, so the two sets could differ) and opened at the band's activeIndex - the leftmost picture of a
     * filmstrip that shows two or three - through slideTo(), which in loop mode lands on a shifted slide.
     * mediaId: the picture to open on; without one, the band's current picture.
     */
    openLightbox(mediaId) {
      if (!this.lightbox) return;
      this._lightboxOpener = document.activeElement; // r71: restore on close

      var lbWrapper = this.lightbox.querySelector('[data-lightbox-swiper] .swiper-wrapper');

      // Cache all lightbox slides on first call; from here the band's set decides (the server's --off was the same rule)
      if (!this._allLightboxSlides) {
        this._allLightboxSlides = Array.from(lbWrapper.querySelectorAll('.swiper-slide'));
        this._allLightboxSlides.forEach(function(s) { s.classList.remove('goessl-lightbox__slide--off'); });
      }
      var byId = {};
      this._allLightboxSlides.forEach(function(slide) { byId[slide.dataset.mediaId] = slide; });

      // Destroy previous lightbox swiper
      if (this.lightboxSwiper) {
        this.lightboxSwiper.destroy(true, true);
        this.lightboxSwiper = null;
      }

      // The band's pictures, in the band's order; without a band (Swiper not loaded) the colour rule of the gallery
      var ids = [];
      if (this.mainSwiper && this.mainSwiper.slides && this.mainSwiper.slides.length) {
        ids = Array.prototype.map.call(this.mainSwiper.slides, function(s) { return s.dataset.mediaId; });
      } else {
        ids = this._allLightboxSlides.filter(this.slideFilter(this._allLightboxSlides, this.getSelectedColorCode()))
          .map(function(s) { return s.dataset.mediaId; });
      }
      ids = ids.filter(function(id) { return byId[id]; });

      while (lbWrapper.firstChild) lbWrapper.removeChild(lbWrapper.firstChild);
      ids.forEach(function(id) { lbWrapper.appendChild(byId[id]); });

      var start = mediaId ? ids.indexOf(String(mediaId)) : -1;
      if (start < 0 && this.mainSwiper) start = Math.min(this.mainSwiper.activeIndex, ids.length - 1);
      if (start < 0) start = 0;

      // Visible first: the lightbox is display:none while aria-hidden="true", and a Swiper built in there measures
      // nothing - slideToLoop then has no grid to move on and stays on the first picture.
      this.lightbox.classList.add('is-open');
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      if (typeof Swiper !== 'undefined') {
        this.initLightboxSwiper(ids.length);
      }

      // In loop mode the position of a picture is looked up by its own index (data-swiper-slide-index) - the loop may
      // have rearranged the slides. Not slideToLoop: Swiper 11 moves there only on the next animation frame, so the
      // first picture flashed up first (and a tab without frames - a background or test browser - never moved at all).
      if (this.lightboxSwiper) {
        var sw = this.lightboxSwiper;
        sw.slideTo(sw.params.loop && sw.getSlideIndexByData ? sw.getSlideIndexByData(start) : start, 0, false);
      }

      this.updateLightboxCounter();
      // r71: move focus into the dialog (close button) — it opened without
      // taking focus, leaving keyboard users behind the overlay
      var lbClose = this.lightbox.querySelector('[data-lightbox-close], button');
      if (lbClose) { try { lbClose.focus(); } catch (e) {} }
    }

    closeLightbox() {
      if (!this.lightbox) return;
      // the band follows to the picture the lightbox was on (22.10.2026)
      if (this.lightboxSwiper && this.mainSwiper) {
        var current = this.lightboxSwiper.slides[this.lightboxSwiper.activeIndex];
        var at = current ? this.bandIndexOf(current.dataset.mediaId) : -1;
        if (at > -1 && at !== this.mainSwiper.activeIndex) this.mainSwiper.slideTo(at, 0);
      }
      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      // r71: hand focus back to whatever opened the lightbox
      if (this._lightboxOpener && this._lightboxOpener.focus) { try { this._lightboxOpener.focus(); } catch (e) {} }
      this._lightboxOpener = null;
      // Reset Swiper zoom so the next open starts un-zoomed (guarded — see slideChange note).
      if (this.lightboxSwiper && this.lightboxSwiper.zoom && this.lightboxSwiper.zoom.scale !== 1) {
        try { this.lightboxSwiper.zoom.out(); } catch (e) { /* already out */ }
      }
    }

    updateLightboxCounter() {
      if (!this.lightboxCounter || !this.lightboxSwiper) return;
      var total = this.lightboxSwiper.slides.length;
      // realIndex: the picture's own position - activeIndex shifts when the loop rearranges the slides
      this.lightboxCounter.textContent = (this.lightboxSwiper.realIndex + 1) + ' / ' + total;
    }

    formatMoney(cents) {
      if (typeof window.Goessl !== 'undefined' && typeof window.Goessl.formatMoney === 'function') {
        return window.Goessl.formatMoney(cents);
      }
      // Fallback matching the theme's suffix format ("1.500,00 \u20AC" \u2014 Figma r9;
      // the shop-level moneyFormat stays \u20AC-prefixed for the live theme).
      var parts = (cents / 100).toFixed(2).split('.');
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return intPart + ',' + parts[1] + ' \u20AC';
    }
  }

  function initProductForms() {
    var sections = document.querySelectorAll('[data-product-section]');
    sections.forEach(function(section) {
      new ProductForm(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductForms);
  } else {
    initProductForms();
  }

  // Lightbox image zoom is now handled by Swiper's built-in Zoom module
  // (initialized in ProductForm.initLightboxSwiper). Gives native pinch on
  // mobile, double-tap toggle, and pan when zoomed — for free.
})();

  /* ── Verfügbarkeit im Geschäft (r38, SJ 23.08; r67 Reservierung) ────
     Lädt die Verfügbarkeit beim Öffnen und bei jedem Variantenwechsel - seit
     06.10.2026 für alle Größen der GEWÄHLTEN Farbe, damit jede Zeile ihre
     Größen nennt (Frau Adler). Geladen und gezeichnet wird in store-reserve.js
     — dort liegen Zeilen, Favorit und die Reservierung, damit die
     Schnellansicht dieselben benutzt statt einer zweiten Fassung. */
  (function () {
    var ENDPOINT = 'https://gwandl-mcp.g-ssl.workers.dev/storefront/store-availability';

    /* Felix 08.09: ob der Online-Shop die gewaehlte Groesse hat, entscheidet
       mit, was Store-Zeile und Modal sagen duerfen. Erste Quelle ist das
       variant:change-Detail (variant:null = Kombination ohne Variante =
       nicht kaufbar); vor dem ersten Event gilt der Serverstand des
       Benachrichtigen-Blocks (sichtbar = ausverkauft). Ohne diesen Block
       (Vorbestellung) ist jede Groesse kaufbar. Dieser Listener steht vor
       allen anderen in der Datei und laeuft deshalb zuerst. */
    var onlineAvail = null;
    function onlineAvailable() {
      if (onlineAvail !== null) return onlineAvail;
      var bis = document.querySelector('[data-bis]');
      return bis ? !!bis.hidden : true;
    }
    document.addEventListener('variant:change', function (e) {
      if (e.detail && ('variant' in e.detail)) onlineAvail = e.detail.variant ? e.detail.variant.available !== false : false;
    });

    document.querySelectorAll('[data-store-availability]').forEach(function (acc) {
      var list = acc.querySelector('[data-stav-list]');
      if (!list) return;
      var productTitle = acc.dataset.productTitle || '';
      var articleNo = acc.dataset.articleNo || '';
      var handle = acc.dataset.handle || '';
      var section = acc.closest('[data-product-section]');
      var product = null;
      try { product = JSON.parse(section.querySelector('[data-product-json]').textContent); } catch (e) { /* ohne Daten: Liste wie frueher, ohne Groessen */ }

      // Die gewaehlten Optionswerte: die der Variante im Formular (die Liste
      // antwortet fuer genau das, was in den Warenkorb ginge - ein ?variant=-Link
      // auf eine Farbe ohne Bild laesst die sichtbare Auswahl davon abweichen);
      // ohne Variante (Farbe ohne diese Groesse) die aktiven Knoepfe, gelesen
      // wie ProductForm.getVariantFromOptions.
      function selectedOptions() {
        var v = currentVariant();
        var hit = v && product.variants.find(function (x) { return String(x.id) === v; });
        if (hit) return hit.options;
        return product.options.map(function (name, i) {
          var g = section.querySelector('[data-option-index="' + i + '"]');
          if (!g) return '';
          if (g.tagName === 'SELECT') return g.value;
          var a = g.querySelector('.active');
          return a ? a.dataset.value : '';
        });
      }
      function sizeGrid() {
        var grid = section && section.querySelector('.goessl-product__size-grid[data-option-name="Größe"]');
        return grid && !grid.hidden ? grid : null;   // die Einheitsgroesse hat ein verstecktes Raster
      }
      // Frau Adler 06.10.2026: alle Groessen der gewaehlten Farbe - die Liste nennt sie je Geschaeft
      function colourSizes() {
        var si = product ? product.options.indexOf('Größe') : -1;
        var grid = sizeGrid();
        if (si < 0 || !grid) return null;
        return window.GoesslReserve.colourSizes(product.variants, selectedOptions(), si,
          Array.from(grid.querySelectorAll('.goessl-product__size-btn')).map(function (b) {
            return { value: b.dataset.value, label: b.textContent.trim() };
          }));
      }

      function currentVariant() {
        var input = document.querySelector('[data-variant-id]') ||
          document.querySelector('form[action*="/cart/add"] [name="id"]');
        return input && input.value ? String(input.value) : null;
      }

      function selectedColor() {
        var c = document.querySelector('[data-selected-color]');
        return c ? c.textContent.trim() : '';
      }
      function selectedSize() {
        var b = document.querySelector('.goessl-product__size-btn.active');
        return b ? b.textContent.trim() : '';
      }

      // Wird bei jedem Öffnen von Seite 2 frisch gelesen, nicht eingefroren:
      // zwischen Modal-Öffnen und Absenden kann die Größe gewechselt haben.
      function reserveCtx() {
        return {
          title: productTitle,
          articleNo: articleNo,
          handle: handle,
          color: selectedColor(),
          size: selectedSize(),
          variantId: currentVariant() || '',
          available: onlineAvailable(),
        };
      }

      var ctrl = window.GoesslReserve && window.GoesslReserve.attach(acc, reserveCtx);
      if (!ctrl) return;

      // Laden, Zwischenspeicher und Zeichnen liegen in store-reserve.js (ctrl.load).
      // Ohne gewaehlte Groesse bleibt es bei der Bitte, eine zu waehlen; eine
      // gewaehlte Groesse, die es in dieser Farbe nicht gibt (Variante ''),
      // zeigt die Geschaefte mit den anderen Groessen der Farbe.
      function load() {
        var v = currentVariant();
        var grid = sizeGrid();
        if (!v && !(grid && grid.querySelector('.goessl-product__size-btn.active'))) {
          ctrl.message(window.g26t('products.store_availability.choose_size_first', 'Bitte wählen Sie zuerst eine Größe.'));
          return;
        }
        ctrl.load(v || '', colourSizes());
      }

      // r60: der Block ist ein Modal (data-stav-modal), geöffnet über den
      // Knopf „Verfügbarkeit prüfen“; der alte <details>-Pfad bleibt für
      // etwaige Akkordeon-Instanzen bestehen.
      var isModal = acc.hasAttribute('data-stav-modal');
      function modalOpen() { return acc.classList.contains('is-open'); }
      // a11y #902: das gemeinsame Overlay-Verhalten (assets/global.js) - der Fokus springt ins Modal
      // (Schließen-Knopf), Tab bleibt drin, Escape schließt, der Fokus kehrt zum öffnenden Knopf zurück
      var overlay = null;
      function closeModal() {
        acc.classList.remove('is-open');
        acc.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        ctrl.reset();   // ein erneutes Öffnen zeigt nie einen alten Erfolgsschirm
        if (overlay) overlay.close();
      }
      if (isModal) {
        document.querySelectorAll('[data-stav-open]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            ctrl.reset();
            acc.classList.add('is-open');
            acc.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
            if (!overlay && window.Goessl && window.Goessl.overlayFocus) overlay = window.Goessl.overlayFocus(acc, { onEscape: closeModal });
            if (overlay) overlay.open(btn);
            load();
          });
        });
        acc.querySelectorAll('[data-stav-close]').forEach(function (el) {
          el.addEventListener('click', closeModal);
        });
      } else {
        acc.addEventListener('toggle', function () { if (acc.open) load(); });
      }
      document.addEventListener('variant:change', function () {
        if (isModal ? modalOpen() : acc.open) load();
      });
    });

    /* r68 (SJ, per Figma): the PDP store line shows the FAVOURITE store's
       availability for the selected variant — „● Auf Lager in Bad Gastein"
       plus „Anderen Store auswählen" (opens the modal). No favourite yet →
       just „Store auswählen". Same endpoint as the modal, cached per variant. */
    var favStockRow = document.querySelector('[data-fav-stock-row]');
    if (favStockRow) (function () {
      var stockSpan = favStockRow.querySelector('[data-fav-stock]');
      var chooser = favStockRow.querySelector('[data-fav-stock-link]');
      var cache = {};
      /* Felix 08.09: eine Groesse, die weder online noch in einem Geschaeft
         vorraetig ist, bekommt kein "Store auswaehlen" und kein "Im Store
         anprobieren" - beides fuehrte in ein Modal, das nichts anbieten
         konnte. Online kaufbar → sofort zeigen; online ausverkauft → erst
         zeigen, wenn der Endpunkt mindestens ein Geschaeft mit Bestand nennt. */
      var storeBlock = favStockRow.closest('.g26-pdp__store');
      var tryOnPill = document.querySelector('.g26-pdp__pill[data-stav-open]');
      function showStoreUi(on) {
        [storeBlock, tryOnPill].forEach(function (el) {
          if (!el) return;
          if (on) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
        });
      }

      function favSlug() {
        try { return localStorage.getItem('goessl:favStore') || null; } catch (e) { return null; }
      }
      function rowVariant() {
        var input = document.querySelector('[data-variant-id]') ||
          document.querySelector('form[action*="/cart/add"] [name="id"]');
        return input && input.value ? String(input.value) : null;
      }
      function escText(v) {
        return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
      }
      function inText(slug, city) {
        // „im Gwandhaus" reads as a sentence; every other store is „in <Stadt>"
        if (slug === 'goessl-im-gwandhaus') return window.g26t('products.store_availability.place_gwandhaus', 'im Gwandhaus');
        return window.g26t('products.store_availability.place_in_city', 'in {{ city }}', { city: escText(city) });
      }
      function prettify(slug) {
        return slug.replace(/^goessl-/, '').split('-').map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ');
      }
      function paintLine(stores, fav) {
        var hit = stores.find(function (s) {
          return String(s.page || '').replace(/[^a-z0-9-]/gi, '') === fav;
        });
        var place = hit ? inText(fav, hit.city || hit.name) : inText(fav, prettify(fav));
        stockSpan.innerHTML = hit
          ? '<span class="goessl-product__stock-status--in-stock">' + window.g26t('products.store_availability.in_stock_at', 'Auf Lager {{ place }}', { place: place }) + '</span>'
          : '<span class="goessl-product__stock-status--out-of-stock">' + window.g26t('products.store_availability.out_of_stock_at', 'Zurzeit nicht vorrätig {{ place }}', { place: place }) + '</span>';
      }
      function renderRow() {
        var fav = favSlug();
        chooser.textContent = fav ? window.g26tText('products.product.store_select_other', 'Anderen Store auswählen') : window.g26tText('products.product.store_select', 'Store auswählen');
        var v = rowVariant();
        var avail = onlineAvailable();
        // Ohne gewaehlte Groesse (oder online kaufbar) bleibt alles sichtbar;
        // online ausverkauft → verstecken, bis ein Geschaeft mit Bestand bekannt ist.
        if (!v || avail) showStoreUi(true);
        else if (cache[v]) showStoreUi(cache[v].length > 0);
        else showStoreUi(false);
        if (!fav) stockSpan.innerHTML = '';
        if (!v) { stockSpan.innerHTML = ''; return; }
        if (cache[v]) { if (fav) paintLine(cache[v], fav); return; }
        if (!fav && avail) return;   // nichts zu malen, nichts zu entscheiden
        fetch(ENDPOINT + '?variant=' + encodeURIComponent(v))
          .then(function (r) { return r.json(); })
          .then(function (j) {
            cache[v] = j.stores || [];
            if (rowVariant() !== v) return;
            if (!onlineAvailable()) showStoreUi(cache[v].length > 0);
            if (favSlug()) paintLine(cache[v], favSlug());
          })
          .catch(function () { stockSpan.innerHTML = ''; if (!onlineAvailable()) showStoreUi(false); });
      }
      document.addEventListener('variant:change', renderRow);
      document.addEventListener('favstore:change', renderRow);
      renderRow();
    })();
  })();

